import { Tag, Wrap, WrapItem } from '@chakra-ui/react'
import { memo, useMemo } from 'react'

import { useUser } from '@/client/context/user'
import { getConversationTitle } from '@/client/services/conversation'
import type { ConversationTitleConversationFragment } from '@/client/services/hasura/generated/graphql'
import { getSeriesUrl, getSessionUrl } from '@/client/services/url'

import OptionalLink from '@/components/Worlds/UI/OptionalLink'

function ConversationTitle({
  conversation,
  includeLinkToEntity = false,
}: {
  conversation: ConversationTitleConversationFragment
  includeLinkToEntity?: boolean
}): React.ReactElement {
  const user = useUser()

  const title = useMemo(
    () => getConversationTitle({ conversation, userId: user.id }),
    [conversation, user.id]
  )
  const isArchived = conversation.current_conversation_user?.[0]?.archived_at != null

  const { series, session } = conversation
  const entityUrl =
    series != null ? getSeriesUrl(series.id) : session != null ? getSessionUrl(session.id) : null

  return (
    <Wrap align="center" spacing="8px">
      <WrapItem>
        <OptionalLink href={includeLinkToEntity ? entityUrl : null} variant="basic" isExternal>
          <b>{title}</b>
        </OptionalLink>
      </WrapItem>
      {isArchived ? (
        <WrapItem>
          <Tag bg="bg.emphasized" color="fg.muted" size="sm">
            Archived
          </Tag>
        </WrapItem>
      ) : null}
    </Wrap>
  )
}

export default memo(
  ConversationTitle,
  (prevProps, nextProps) =>
    prevProps.conversation.id === nextProps.conversation.id &&
    prevProps.conversation.current_conversation_user?.[0]?.archived_at ===
      nextProps.conversation.current_conversation_user?.[0]?.archived_at
)
